import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MainHeader from "../components/MainHeader";
import MainFooter from "../components/MainFooter";
import { Clock, Ticket, Clapperboard, Play } from "lucide-react";

export default function Library() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove("theme-mini");
  }, []);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/library").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, [user]);

  if (user === null) return null;
  if (user === false) return <Navigate to="/login?redirect=/library" replace />;

  return (
    <div data-testid="library-page" className="min-h-screen bg-gs-cream">
      <MainHeader />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-ember">Your ticket stub</p>
        <h1 className="font-serif text-5xl text-gs-ink mt-3">Your Library</h1>
        <p className="font-sans text-gs-ink/70 mt-3">Films you've rented or own. Click any title to walk back into the theater.</p>

        {loading ? (
          <p className="mt-12 font-mono text-xs uppercase tracking-widest text-gs-dust">Loading…</p>
        ) : items.length === 0 ? (
          <div className="mt-16 border border-dashed border-gs-line rounded-md p-16 text-center">
            <p className="font-serif text-2xl text-gs-ink/70">Your library is dim.</p>
            <Link to="/" className="inline-block mt-6 bg-gs-ink text-gs-cream rounded-none px-6 py-3 text-xs font-mono uppercase tracking-[0.25em] border border-gs-ember/50 hover:bg-gs-ember hover:text-gs-ink transition-colors">Browse the marquee</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {items.map((it, idx) => (
              <Link
                to={`/film/${it.slug}`}
                key={it.id}
                data-testid={`library-item-${idx}`}
                className="group flex gap-5 bg-gs-paper border border-gs-line rounded-md overflow-hidden hover:border-gs-ember transition-colors"
              >
                <div className="w-32 aspect-[3/4] shrink-0 bg-gs-ink overflow-hidden">
                  <img src={it.film.poster_url} alt={it.film.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="flex-1 p-5">
                  <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest mb-2">
                    {it.mode === "buy" ? (
                      <span className="inline-flex items-center gap-1 text-gs-ember"><Ticket size={10} /> owned</span>
                    ) : it.active ? (
                      <span className="inline-flex items-center gap-1 text-gs-marigold"><Clapperboard size={10} /> rental active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gs-dust"><Clock size={10} /> expired</span>
                    )}
                  </div>
                  <p className="font-display text-3xl text-gs-ink tracking-wide">{it.film.title}</p>
                  <p className="font-serif italic text-gs-ink/70">{it.film.tagline}</p>
                  {it.mode === "rent" && it.expires_at && (
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust mt-3">
                      {it.active ? "Ends" : "Ended"} {new Date(it.expires_at).toLocaleString()}
                    </p>
                  )}
                  <span className="inline-flex items-center gap-1 mt-4 text-gs-ember text-sm">
                    <Play size={12} /> Enter the theater
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <MainFooter />
    </div>
  );
}
